import { useCallback, useRef } from 'react';

type DragDirection = 'horizontal' | 'vertical';

interface UseDragToAdjustOptions {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  direction?: DragDirection;
}

interface UseDragToAdjustReturn {
  handleMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
  isDragging: boolean;
  cursor: string;
}

const PIXELS_PER_STEP = 2;
const DRAG_THRESHOLD = 3;

export const useDragToAdjust = ({
  min,
  max,
  step,
  value,
  onChange,
  direction = 'vertical'
}: UseDragToAdjustOptions): UseDragToAdjustReturn => {
  const isDraggingRef = useRef(false);
  const hasDragStartedRef = useRef(false);
  const startPositionRef = useRef(0);
  const startValueRef = useRef(0);
  const lastOnChangeValueRef = useRef<number | null>(null);

  const isVertical = direction === 'vertical';
  const cursorStyle = isVertical ? 'ns-resize' : 'ew-resize';

  const clamp = (val: number, minVal: number, maxVal: number): number => {
    return Math.min(Math.max(val, minVal), maxVal);
  };

  const roundToStep = (val: number, stepSize: number): number => {
    if (stepSize < 1) {
      const precision = Math.ceil(-Math.log10(stepSize));
      const multiplier = Math.pow(10, precision);
      return Math.round(val * multiplier) / multiplier;
    }
    return Math.round(val);
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.button !== 0) {
        return;
      }

      isDraggingRef.current = true;
      hasDragStartedRef.current = false;
      startPositionRef.current = isVertical ? e.clientY : e.clientX;
      startValueRef.current = value;
      lastOnChangeValueRef.current = value;

      const targetElement = e.currentTarget;
      const inputElement = targetElement.querySelector('input');

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDraggingRef.current) {
          return;
        }

        const currentPosition = isVertical ? moveEvent.clientY : moveEvent.clientX;
        const delta = isVertical
          ? startPositionRef.current - currentPosition
          : currentPosition - startPositionRef.current;

        if (!hasDragStartedRef.current && Math.abs(delta) >= DRAG_THRESHOLD) {
          hasDragStartedRef.current = true;
          inputElement?.blur();
          document.body.style.cursor = cursorStyle;
          document.body.style.userSelect = 'none';
        }

        if (hasDragStartedRef.current) {
          const steps = Math.floor(delta / PIXELS_PER_STEP);
          const newValue = startValueRef.current + steps * step;
          const clampedValue = clamp(newValue, min, max);
          const roundedValue = roundToStep(clampedValue, step);

          if (roundedValue !== lastOnChangeValueRef.current) {
            lastOnChangeValueRef.current = roundedValue;
            onChange(roundedValue);
          }
        }
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        hasDragStartedRef.current = false;
        lastOnChangeValueRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [value, step, min, max, onChange, isVertical, cursorStyle]
  );

  return {
    handleMouseDown,
    isDragging: isDraggingRef.current,
    cursor: cursorStyle
  };
};
